'use client'

import { useEffect, useRef } from 'react'

// ─── Configurable Parameters ───────────────────────────────────────────────
export const GEOMETRIC_CONFIG = {
  /** Number of geometric elements on screen (6–10 recommended) */
  shapeCount: 9,
  /** Base movement speed multiplier (1 = normal) */
  speed: 0.8,
  /** Maximum opacity for filled shapes (0.08–0.18 recommended) */
  maxOpacity: 0.12,
  /** Mouse repulsion radius in pixels */
  mouseRadius: 180,
  /** Mouse repulsion strength (higher = stronger push) */
  mouseStrength: 0.5,
  /** Whether to show on mobile (< 768px). Set false to disable. */
  enableMobile: true,
  /** Probability (0–1) that a shape is stroke-only (outline) vs filled */
  outlineChance: 0.3,
  /** Multiplier applied to shape sizes on mobile (< 768px). */
  mobileSizeScale: 0.55,
}

/** Optional overrides for specific use cases (e.g. footer) */
export interface GeometricBackgroundProps {
  /** Override config values for this instance */
  configOverrides?: Partial<typeof GEOMETRIC_CONFIG>
  /** Custom gradient palette for shapes */
  customGradients?: [string, string][]
  /** Position shapes across the full width instead of just the right side */
  fullWidth?: boolean
  /** Separate config overrides applied only on mobile (< 768px) */
  mobileConfig?: Partial<typeof GEOMETRIC_CONFIG>
}

// ─── Types ─────────────────────────────────────────────────────────────────
type ShapeType = 'circle' | 'roundedRect' | 'line' | 'dot' | 'arc' | 'polygon'

interface Shape {
  type: ShapeType
  x: number
  y: number
  vx: number
  vy: number
  rotation: number
  rotationSpeed: number
  size: number
  strokeWidth: number
  opacity: number
  sides: number // for polygon
  arcAngle: number // for arc
  cornerRadius: number // for roundedRect
  phase: number // for gentle floating
  /** Gradient start color (hex) */
  gradientStart: string
  /** Gradient end color (hex) */
  gradientEnd: string
  /** true = solid fill (transparent), false = stroke-only outline */
  filled: boolean
}

// ─── Gradient Palette ──────────────────────────────────────────────────────
// Modern High-Energy Electric Blue-Violet & Iris Purple palette — sleek, trendy, vibrant
const GRADIENTS: [string, string][] = [
  ['#6366f1', '#a855f7'], // Electric Indigo → Vibrant Violet
  ['#3b82f6', '#8b5cf6'], // Cobalt Blue → Royal Purple
  ['#4f46e5', '#c084fc'], // Deep Sapphire → Light Neon Purple
  ['#00d2ff', '#7c3aed'], // Cyan-Blue → Vivid Electric Violet
  ['#6366f1', '#ec4899'], // Indigo → Electric Fuchsia
  ['#2563eb', '#a855f7'], // Electric Blue → Bright Violet
]

// ─── Component ─────────────────────────────────────────────────────────────
export default function GeometricBackground({
  configOverrides,
  customGradients,
  fullWidth,
  mobileConfig,
}: GeometricBackgroundProps = {}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const shapesRef = useRef<Shape[]>([])
  const mouseRef = useRef({ x: -9999, y: -9999 })
  const rafRef = useRef<number>(0)
  const isMobileRef = useRef(false)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const config = {
      ...GEOMETRIC_CONFIG,
      ...configOverrides,
      ...(isMobileRef.current ? mobileConfig : {}),
    }
    const gradients = customGradients ?? GRADIENTS
    let lastTime = 0

    // ── Check mobile ──────────────────────────────────────────────────────
    const checkMobile = () => {
      isMobileRef.current = window.innerWidth < 768
    }
    checkMobile()

    // ── Resize handler ─────────────────────────────────────────────────────
    const resize = () => {
      checkMobile()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = window.innerWidth
      const h = window.innerHeight
      canvas!.width = w * dpr
      canvas!.height = h * dpr
      canvas!.style.width = `${w}px`
      canvas!.style.height = `${h}px`
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    resize()
    window.addEventListener('resize', resize)

    // ── Initialize shapes ──────────────────────────────────────────────────
    const initShapes = () => {
      const w = window.innerWidth
      const h = window.innerHeight
      // On mobile: only shape types, no lines/dots — cleaner for small screens
      const types: ShapeType[] = isMobileRef.current
        ? ['circle', 'roundedRect', 'arc', 'polygon']
        : ['circle', 'roundedRect', 'line', 'dot', 'arc', 'polygon']
      const shapes: Shape[] = []

      for (let i = 0; i < config.shapeCount; i++) {
        // First shape is always a large circle on the right side
        const isFirstCircle = i === 0
        // Last shape is the hero piece — extra-large on the far right
        const isHero = i === config.shapeCount - 1

        // When there's only one shape, use roundedRect (square) instead of circle
        const type =
          config.shapeCount === 1
            ? 'roundedRect'
            : isFirstCircle
              ? 'circle'
              : types[i % types.length]

        // Position: fullWidth spreads across entire viewport, otherwise right side only
        const x = fullWidth ? w * (0.05 + Math.random() * 0.9) : w * (0.55 + Math.random() * 0.4)
        const y = h * (0.05 + Math.random() * 0.9)

        const mobileScale = isMobileRef.current ? config.mobileSizeScale : 1
        const size =
          (isHero
            ? 320 + Math.random() * 200
            : isFirstCircle
              ? 220 + Math.random() * 120
              : 100 + Math.random() * 250) * mobileScale
        const posX = fullWidth ? x : isHero ? w * (0.78 + Math.random() * 0.18) : x
        const posY = fullWidth ? y : isHero ? h * (0.2 + Math.random() * 0.4) : y

        // Every shape gets a blue-purple gradient
        const gradientPair = gradients[Math.floor(Math.random() * gradients.length)]

        // Determine if this shape is stroke-only (outline) or filled
        // The hero piece and first circle are always filled; others have a chance to be outline
        const filled = isHero || isFirstCircle ? true : Math.random() > config.outlineChance

        shapes.push({
          type,
          x: posX,
          y: posY,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.008,
          size,
          strokeWidth: isHero ? 2.5 + Math.random() * 2 : 2 + Math.random() * 3,
          opacity: isHero
            ? config.maxOpacity * (0.8 + Math.random() * 0.2)
            : config.maxOpacity * (0.6 + Math.random() * 0.4),
          sides: 3 + Math.floor(Math.random() * 5), // 3–7 sides
          arcAngle: Math.PI * (0.25 + Math.random() * 1.5),
          cornerRadius: 6 + Math.random() * 16,
          phase: Math.random() * Math.PI * 2,
          gradientStart: gradientPair[0],
          gradientEnd: gradientPair[1],
          filled,
        })
      }
      shapesRef.current = shapes
    }
    initShapes()

    // ── Mouse handlers ─────────────────────────────────────────────────────
    const onMouseMove = (e: MouseEvent) => {
      if (isMobileRef.current) return
      mouseRef.current = { x: e.clientX, y: e.clientY }
    }
    const onMouseLeave = () => {
      mouseRef.current = { x: -9999, y: -9999 }
    }
    const onTouchStart = () => {
      // Disable mouse interaction on touch devices
      mouseRef.current = { x: -9999, y: -9999 }
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseleave', onMouseLeave)
    window.addEventListener('touchstart', onTouchStart, { passive: true })

    // ── Visibility change ──────────────────────────────────────────────────
    let isVisible = true
    const onVisibility = () => {
      isVisible = !document.hidden
    }
    document.addEventListener('visibilitychange', onVisibility)

    // ── Reduced motion ─────────────────────────────────────────────────────
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)')
    let reducedMotion = prefersReduced.matches
    const onMotionChange = (e: MediaQueryListEvent) => {
      reducedMotion = e.matches
    }
    prefersReduced.addEventListener('change', onMotionChange)

    // ── Draw ───────────────────────────────────────────────────────────────
    const draw = (time: number) => {
      if (!isVisible) {
        rafRef.current = requestAnimationFrame(draw)
        return
      }

      const dt = Math.min((time - lastTime) / 16.667, 3) // normalize to ~60fps
      lastTime = time

      const w = window.innerWidth
      const h = window.innerHeight

      ctx!.clearRect(0, 0, w, h)

      // On mobile with animation disabled, just draw static
      if ((isMobileRef.current && !config.enableMobile) || reducedMotion) {
        shapesRef.current.forEach((shape) => {
          drawShape(ctx!, shape, w, h, 0)
        })
        rafRef.current = requestAnimationFrame(draw)
        return
      }

      const speedFactor = config.speed * dt
      const mouse = mouseRef.current

      shapesRef.current.forEach((shape) => {
        // ── Mouse repulsion ────────────────────────────────────────────────
        const dx = shape.x - mouse.x
        const dy = shape.y - mouse.y
        const dist = Math.sqrt(dx * dx + dy * dy)
        if (dist < config.mouseRadius && dist > 0) {
          const force = (1 - dist / config.mouseRadius) * config.mouseStrength
          shape.vx += (dx / dist) * force * speedFactor
          shape.vy += (dy / dist) * force * speedFactor
        }

        // ── Damping ────────────────────────────────────────────────────────
        shape.vx *= 0.98
        shape.vy *= 0.98

        // ── Gentle floating oscillation ────────────────────────────────────
        shape.vx += Math.sin(time * 0.0003 + shape.phase) * 0.008 * speedFactor
        shape.vy += Math.cos(time * 0.0004 + shape.phase) * 0.008 * speedFactor

        // ── Move ───────────────────────────────────────────────────────────
        shape.x += shape.vx * speedFactor
        shape.y += shape.vy * speedFactor
        shape.rotation += shape.rotationSpeed * speedFactor

        // ── Wrap around edges (soft) ───────────────────────────────────────
        const margin = shape.size * 2
        if (shape.x < -margin) shape.x = w + margin
        if (shape.x > w + margin) shape.x = -margin
        if (shape.y < -margin) shape.y = h + margin
        if (shape.y > h + margin) shape.y = -margin

        drawShape(ctx!, shape, w, h, time)
      })

      rafRef.current = requestAnimationFrame(draw)
    }

    rafRef.current = requestAnimationFrame(draw)

    // ── Cleanup ────────────────────────────────────────────────────────────
    return () => {
      cancelAnimationFrame(rafRef.current)
      window.removeEventListener('resize', resize)
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseleave', onMouseLeave)
      window.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('visibilitychange', onVisibility)
      prefersReduced.removeEventListener('change', onMotionChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <canvas ref={canvasRef} className="geometric-bg-canvas" aria-hidden="true" />
}

// ─── Drawing Helpers ───────────────────────────────────────────────────────

function drawShape(
  ctx: CanvasRenderingContext2D,
  shape: Shape,
  _w: number,
  _h: number,
  _time: number
) {
  ctx.save()
  ctx.translate(shape.x, shape.y)
  ctx.rotate(shape.rotation)

  ctx.globalAlpha = shape.opacity
  ctx.lineWidth = shape.strokeWidth
  ctx.shadowColor = shape.gradientStart
  ctx.shadowBlur = shape.filled ? 25 : 15

  // ── Create blue-purple gradient ─────────────────────────────────────────
  const halfSize = shape.size / 2
  const grad = ctx.createLinearGradient(-halfSize, -halfSize, halfSize, halfSize)
  grad.addColorStop(0, shape.gradientStart)
  grad.addColorStop(1, shape.gradientEnd)

  // ── For filled shapes: use a semi-transparent gradient fill ─────────────
  // ── For outline shapes: use gradient stroke only ────────────────────────
  if (shape.filled) {
    ctx.fillStyle = grad
  } else {
    ctx.strokeStyle = grad
  }

  switch (shape.type) {
    case 'circle':
      ctx.beginPath()
      ctx.arc(0, 0, shape.size / 2, 0, Math.PI * 2)
      if (shape.filled) ctx.fill()
      else ctx.stroke()
      break

    case 'dot':
      ctx.beginPath()
      ctx.arc(0, 0, shape.size / 6, 0, Math.PI * 2)
      ctx.fill()
      break

    case 'roundedRect':
      const r = shape.cornerRadius
      const s = shape.size
      ctx.beginPath()
      ctx.moveTo(-s / 2 + r, -s / 2)
      ctx.lineTo(s / 2 - r, -s / 2)
      ctx.quadraticCurveTo(s / 2, -s / 2, s / 2, -s / 2 + r)
      ctx.lineTo(s / 2, s / 2 - r)
      ctx.quadraticCurveTo(s / 2, s / 2, s / 2 - r, s / 2)
      ctx.lineTo(-s / 2 + r, s / 2)
      ctx.quadraticCurveTo(-s / 2, s / 2, -s / 2, s / 2 - r)
      ctx.lineTo(-s / 2, -s / 2 + r)
      ctx.quadraticCurveTo(-s / 2, -s / 2, -s / 2 + r, -s / 2)
      ctx.closePath()
      if (shape.filled) ctx.fill()
      else ctx.stroke()
      break

    case 'line':
      ctx.beginPath()
      ctx.moveTo(-shape.size / 2, 0)
      ctx.lineTo(shape.size / 2, 0)
      ctx.stroke()
      break

    case 'arc':
      ctx.beginPath()
      ctx.arc(0, 0, shape.size / 2, -shape.arcAngle / 2, shape.arcAngle / 2)
      ctx.stroke()
      break

    case 'polygon':
      ctx.beginPath()
      for (let i = 0; i < shape.sides; i++) {
        const angle = (i / shape.sides) * Math.PI * 2 - Math.PI / 2
        const px = Math.cos(angle) * (shape.size / 2)
        const py = Math.sin(angle) * (shape.size / 2)
        if (i === 0) ctx.moveTo(px, py)
        else ctx.lineTo(px, py)
      }
      ctx.closePath()
      if (shape.filled) ctx.fill()
      else ctx.stroke()
      break
  }

  ctx.restore()
}
